import React, { useEffect, useState } from "react";
import "./styles/index.scss";
import { connect } from "react-redux";
import Actions from "../../../actions";
import AddPersonToDealForm from "../inviteMember/AddPersonToDealForm";
import AddAddress from "../addAddress";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { clickToLinkHandler, simpleSort } from "../../../helpers/utils";
import Button from "../../common/forms/button";
import Delimiter from "../../common/forms/delimiter/Delimiter";
import * as api from "../../../api/generated-api";
import { PersonCreate } from "../../../api/generated-api";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../api/axiosConfig";
import { processUnexpectedError } from "../../../api/errors";
import DealMemberRow from "../common/DealMemberRow";
import {
  addExistingPersonAsDealCompanyMember,
  addNewPersonAsDealCompanyMember,
  getDealCompanyMembers,
  removeDealCompanyMember,
} from "../../../api/deals/companyMembers";
import { getCompanyByOrgId, updateCompany } from "../../../api/company/company";
import { isCompanyMemberOrCounselForDeal } from "../../../helpers/utilsTS";
import { createDealLawFirmWithMember } from "../../../api/deals/lawfirms";
import { getDealLegalEntitiesForUser } from "../../../api/deals/deallegalentities";
import { UserWithPersonWithOrganizationPersons } from "../../../api/types";
import { getPersonsByOrganizationSearch } from "../../../api/persons/persons";

const getUserName = (person: api.Person) => {
  return `${person.firstName} ${person.lastName}`;
};

type DealCompanyInformationProps = {
  orgId: number;
  dealId: number;
  user: UserWithPersonWithOrganizationPersons;
  updateCompanyMember: (
    dealId: number,
    memberId: number,
    data,
    companyName: string
  ) => void;
  sendPersonInvitation: (
    dealId: number,
    personId: number,
    investorId?: number
  ) => void;
  fieldErrors: any;
  dealMemberUpdateActionStatus: any;
  persons: api.Person[];
  token: string;
  updated: boolean;
};

const DealCompanyInformationPage = ({
  orgId,
  dealId,
  user,
  sendPersonInvitation,
  dealMemberUpdateActionStatus,
  token,
  updated,
}: DealCompanyInformationProps) => {
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState<api.CompanyUpdate>(null);
  const [chiefExecutiveOfficer, setChiefExecutiveOfficer] =
    useState<api.DealLegalEntity>(null);
  const [secretary, setSecretary] = useState<api.DealLegalEntity>(null);
  const [boardMembers, setBoardMembers] = useState<api.DealLegalEntity[]>([]);
  const [keyCommonHolders, setKeyCommonHolders] = useState<
    api.DealLegalEntity[]
  >([]);
  const [companyCounsel, setCompanyCounsel] = useState<api.DealLegalEntity[]>(
    []
  );
  const [titleOfMember, setTitleOfMember] = useState("");
  const [isOpenAddAddress, setIsOpenAddAddress] = useState(false);
  const [canAddUsers, setCanAddUsers] = useState(false);
  const [editMember, setEditMember] = useState<api.DealLegalEntity>(null);
  const [removeMember, setRemoveMember] = useState<api.DealLegalEntity>(null);
  const [dealLegalEntitiesForUser, setDealLegalEntitiesForUser] = useState<
    api.DealLegalEntity[]
  >([]);
  const [persons, setPersons] = useState<api.Person[]>([]);

  //TODO: this is a large file with poorly factored components; suggesting breaking up into 1) CompanyMembersForRole which could be used for each company member type CEO, Secretary, ... 2) CompanyCounselMembers and 3) CompanyAddress the next time there's a large change to this file

  useEffect(() => {
    getDealLegalEntitiesForUser(orgId, dealId, (fetchedDealLegalEntities) => {
      setDealLegalEntitiesForUser(fetchedDealLegalEntities);
    });
  }, [orgId, dealId]);

  const ADD_COMPANY_COUNSEL_TITLE = "Add Company Counsel";

  configureBaseUrl();
  configureAuthHeader(token);

  const fetchDealCompanyMembers = () => {
    getDealCompanyMembers(orgId, dealId, (updatedDealCompanyMembers) => {
      saveDealCompanyMembers(updatedDealCompanyMembers);
    });
  };

  useEffect(() => {
    if (updated) {
      fetchDealCompanyMembers();
    }
  }, [updated]);

  const saveAndUpdateCompany = (orgId: number, company: api.CompanyUpdate) => {
    updateCompany(orgId, company, (updatedCompany) => {
      saveCompanyInformation(updatedCompany);
    });
  };

  const addCompanyAddress = (
    orgId: number,
    companyAddress: api.CompanyUpdate
  ) => {
    saveAndUpdateCompany(orgId, {
      line: companyAddress.line,
      city: companyAddress.city,
      state: companyAddress.state,
      zip: companyAddress.zip,
    });
  };

  const updateMember = async (member: api.DealLegalEntity) => {
    if (
      member.legalEntityType === api.LegalEntityType.Chief_Executive_Officer
    ) {
      setChiefExecutiveOfficer(member);
      setCanAddUsers(true);
    } else if (member.legalEntityType === api.LegalEntityType.Secretary) {
      setSecretary(member);
    } else {
      await fetchDealCompanyMembers();
    }
  };

  const saveAndUpdateNewDealCompanyMember = async (
    orgId: number,
    member: api.LegalEntityAndPersonCreate
  ) => {
    addNewPersonAsDealCompanyMember(
      orgId,
      dealId,
      member,
      async (newMember) => {
        await updateMember(newMember);
      }
    );
  };

  const createDealCompanyMember = (
    orgId: number,
    member: api.LegalEntityAndPersonCreate
  ) => {
    saveAndUpdateNewDealCompanyMember(orgId, member).catch((error: Error) => {
      processUnexpectedError(error);
    });
  };

  const createCompanyCounsel = (
    orgId: number,
    dealId: number,
    firstName: string,
    lastName: string,
    email: string
  ) => {
    createDealLawFirmWithMember(
      orgId,
      dealId,
      {
        dealId,
        email,
        firstName,
        lastName,
        name: `Company Counsel`,
      },
      () => {
        fetchDealCompanyMembers();
      }
    );
  };

  const saveAndAddExistingPersonAsDealCompanyMember = async (
    orgId: number,
    personId: number,
    legalEntityType: api.LegalEntityType
  ) => {
    addExistingPersonAsDealCompanyMember(
      orgId,
      dealId,
      {
        personId,
        legalEntityType,
      },
      async (addedMember) => {
        await updateMember(addedMember);
      }
    );
  };

  const createCompanyMemberForExistingPerson = (
    orgId: number,
    personId: number,
    position: api.LegalEntityType
  ) => {
    saveAndAddExistingPersonAsDealCompanyMember(
      orgId,
      personId,
      position
    ).catch((error: Error) => {
      processUnexpectedError(error);
    });
  };

  const saveCompanyInformation = (company: api.Company) => {
    setCompanyName(company.name);
    if (company.line && company.city && company.state && company.zip) {
      setCompanyAddress({
        line: company.line,
        city: company.city,
        state: company.state,
        zip: company.zip,
      });
    }
  };

  const saveDealCompanyMembers = (companyMembers: api.DealLegalEntity[]) => {
    if (!companyMembers) {
      return;
    }

    setCanAddUsers(companyMembers.length > 0);

    const ceo = companyMembers.filter((companyMember) => {
      if (
        companyMember.legalEntityType ===
        api.LegalEntityType.Chief_Executive_Officer
      ) {
        return companyMember;
      }
    });

    if (ceo?.length === 1) {
      setChiefExecutiveOfficer(ceo[0]);
    } else {
      setChiefExecutiveOfficer(null);
    }

    const secretary = companyMembers.filter((companyMember) => {
      if (companyMember.legalEntityType === api.LegalEntityType.Secretary) {
        return companyMember;
      }
    });

    if (secretary?.length === 1) {
      setSecretary(secretary[0]);
    } else {
      setSecretary(null);
    }

    const keyCommonHolders = companyMembers.filter((companyMember) => {
      if (
        companyMember.legalEntityType === api.LegalEntityType.Key_Common_Holder
      ) {
        return companyMember;
      }
    });

    setKeyCommonHolders(keyCommonHolders);

    const boardMembers = companyMembers.filter((companyMember) => {
      if (companyMember.legalEntityType === api.LegalEntityType.Board_Member) {
        return companyMember;
      }
    });

    setBoardMembers(boardMembers);

    const fetchedCompanyCounsel = companyMembers.filter((companyMember) => {
      if (
        companyMember.legalEntityType ===
        api.LawFirmLegalEntityType.Company_Counsel
      ) {
        return companyMember;
      }
    });

    setCompanyCounsel(fetchedCompanyCounsel);
  };

  const removeDealCompanyMemberAndRefresh = async (
    dealCompanyMemberId: number
  ) => {
    removeDealCompanyMember(orgId, dealId, dealCompanyMemberId, async () => {
      await fetchDealCompanyMembers();
    });
  };

  const searchPersons = (searchString: string) => {
    getPersonsByOrganizationSearch(orgId, searchString, (fetchedPersons) => {
      setPersons(fetchedPersons);
    });
  };

  useEffect(() => {
    const fetchCurrentCompany = async () => {
      getCompanyByOrgId(orgId, (fetchedCompany) => {
        saveCompanyInformation(fetchedCompany);
      });
    };

    fetchCurrentCompany().catch((error: Error) => {
      processUnexpectedError(error);
    });

    fetchDealCompanyMembers();
  }, []);

  useEffect(() => {
    if (
      dealMemberUpdateActionStatus.isRequesting === false &&
      dealMemberUpdateActionStatus.isSuccess === true
    ) {
      setEditMember(null);
    }
  }, [dealMemberUpdateActionStatus]);

  useEffect(() => {
    if (editMember === null) {
      fetchDealCompanyMembers();
    }
  }, [editMember]);

  useEffect(() => {
    if (removeMember === null) {
      fetchDealCompanyMembers();
    } else {
      if (removeMember == chiefExecutiveOfficer) {
        setChiefExecutiveOfficer(null);
      }

      if (removeMember == secretary) {
        setSecretary(null);
      }

      setRemoveMember(null);
      removeDealCompanyMemberAndRefresh(removeMember.id);
    }
  }, [removeMember]);

  if (!dealLegalEntitiesForUser) {
    return null;
  }

  const isUserCompanyMember = isCompanyMemberOrCounselForDeal(
    user.person.organizations_persons,
    orgId,
    dealId,
    dealLegalEntitiesForUser
  );

  return (
    <div className="deals-company-information-page">
      <div className="deals-company-information-title">Company Information</div>
      <Delimiter type="headline" shift="18" />
      <div className="company-information-container">
        <div className="company-information-row">
          <div className="company-information-column">
            <div className="title">Name</div>
            <div id="deal-company-name" className="value">
              {companyName}
            </div>
          </div>
          <div className="company-information-column">
            <div className="title">Company Address</div>
            {companyAddress && (
              <div className="value">
                <div id="deal-company-street-address">
                  {companyAddress.line}
                </div>
                <div id="deal-company-city-state-zip-address">{`${companyAddress.city}, ${companyAddress.state} ${companyAddress.zip}`}</div>
              </div>
            )}
            {isUserCompanyMember && !companyAddress && (
              <Button
                data-testid="add-address-button"
                type="secondary"
                size="small"
                onClick={() => {
                  setIsOpenAddAddress(true);
                }}
              >
                add
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="company-officers-container">
        <div className="header-of-members">
          <div className="title">Company Officers</div>
          <div className="company-officers-buttons-container">
            {isUserCompanyMember && !chiefExecutiveOfficer && (
              <Button
                data-testid="add-ceo-button"
                type="secondary"
                size="medium"
                onClick={() => {
                  setTitleOfMember("Add Chief Executive Officer");
                }}
              >
                Add CEO
              </Button>
            )}

            {isUserCompanyMember && !secretary && (
              <Button
                data-testid={
                  (canAddUsers && "add-secretary-button") ||
                  "add-secretary-button-disabled"
                }
                type={(canAddUsers && "secondary") || "disabled-secondary"}
                size="medium"
                data-tip
                data-for="addUserIsImpossible"
                onClick={() => {
                  if (canAddUsers) {
                    setTitleOfMember("Add Secretary");
                  }
                }}
              >
                Add secretary
              </Button>
            )}
          </div>
        </div>
        {(!!chiefExecutiveOfficer || !!secretary) && (
          <Delimiter type="internal" shift="16" />
        )}

        {(chiefExecutiveOfficer || secretary) && (
          <div className="members-list">
            {chiefExecutiveOfficer && (
              <DealMemberRow
                deletable={isUserCompanyMember}
                invitable={isUserCompanyMember}
                dealMember={chiefExecutiveOfficer}
                showHeader={true}
                editable={isUserCompanyMember}
                onInvite={() =>
                  sendPersonInvitation(dealId, chiefExecutiveOfficer.person.id)
                }
                onRemove={() => {
                  setRemoveMember(chiefExecutiveOfficer);
                }}
              />
            )}

            {secretary && (
              <DealMemberRow
                deletable={isUserCompanyMember}
                invitable={isUserCompanyMember}
                dealMember={secretary}
                editable={isUserCompanyMember}
                onInvite={() =>
                  sendPersonInvitation(dealId, secretary.person.id)
                }
                onRemove={() => {
                  setRemoveMember(secretary);
                }}
              />
            )}
          </div>
        )}
      </div>
      <div className="board-members-container">
        <div className="header-of-members">
          <div className="title">Board Members</div>
          {isUserCompanyMember && (
            <Button
              data-testid="add-board-member-button"
              type={(canAddUsers && "secondary") || "disabled-secondary"}
              size="medium"
              data-tip
              data-for="addUserIsImpossible"
              onClick={() => {
                if (canAddUsers) {
                  setTitleOfMember("Add Board Member");
                }
              }}
            >
              add
            </Button>
          )}
        </div>
        {!!boardMembers.length && <Delimiter type="internal" shift="16" />}
        {!!boardMembers.length && (
          <div className="members-list">
            {boardMembers
              .sort((a, b) => {
                const memberA = getUserName(a.person);
                const memberB = getUserName(b.person);
                return simpleSort(memberA.toLowerCase(), memberB.toLowerCase());
              })
              .map((member, key) => {
                return (
                  <React.Fragment key={"boarding_members_" + key}>
                    <DealMemberRow
                      deletable={isUserCompanyMember}
                      invitable={isUserCompanyMember}
                      dealMember={member}
                      showHeader={key === 0}
                      editable={isUserCompanyMember}
                      onInvite={() => {
                        sendPersonInvitation(dealId, member.person.id);
                      }}
                      onRemove={() => {
                        setRemoveMember(member);
                      }}
                    />
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>
      <div className="key-common-holders-container">
        <div className="header-of-members">
          <div className="title">Key Common Holders</div>
          {isUserCompanyMember && (
            <Button
              data-testid="add-key-common-holder-button"
              type={(canAddUsers && "secondary") || "disabled-secondary"}
              size="medium"
              data-tip
              data-for="addUserIsImpossible"
              onClick={() => {
                if (canAddUsers) {
                  setTitleOfMember("Add Key Common Holder");
                }
              }}
            >
              add
            </Button>
          )}
        </div>
        {!!keyCommonHolders.length && <Delimiter type="internal" shift="16" />}
        {!!keyCommonHolders.length && (
          <div className="members-list">
            {keyCommonHolders
              .sort((a, b) => {
                const memberA = getUserName(a.person);
                const memberB = getUserName(b.person);
                return simpleSort(memberA.toLowerCase(), memberB.toLowerCase());
              })
              .map((member, key) => {
                return (
                  <React.Fragment key={"key_common_holders_" + key}>
                    <DealMemberRow
                      deletable={isUserCompanyMember}
                      invitable={isUserCompanyMember}
                      dealMember={member}
                      showHeader={key === 0}
                      editable={isUserCompanyMember}
                      onInvite={() =>
                        sendPersonInvitation(dealId, member.person.id)
                      }
                      onRemove={() => {
                        setRemoveMember(member);
                      }}
                    />
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>
      <div className="law-firms-container">
        <div className="header-of-members">
          <div className="title">Company Counsel</div>
          {isUserCompanyMember && (
            <Button
              data-testid="add-law-firm-button"
              type="secondary"
              size="medium"
              data-tip
              data-for="addUserIsImpossible"
              onClick={() => {
                setTitleOfMember(ADD_COMPANY_COUNSEL_TITLE);
              }}
            >
              add
            </Button>
          )}
        </div>
        {!!companyCounsel.length && <Delimiter type="internal" shift="16" />}
        {!!companyCounsel.length && (
          <div className="members-list">
            {companyCounsel
              .sort((a, b) => {
                const memberA = getUserName(a.person);
                const memberB = getUserName(b.person);
                return simpleSort(memberA.toLowerCase(), memberB.toLowerCase());
              })
              .map((member, key) => {
                return (
                  <React.Fragment key={"company_counsel_" + key}>
                    <DealMemberRow
                      deletable={isUserCompanyMember}
                      invitable={isUserCompanyMember}
                      dealMember={member}
                      showHeader={key === 0}
                      editable={isUserCompanyMember}
                      canConfigureSignatureBlock={true}
                      onInvite={() =>
                        sendPersonInvitation(dealId, member.person.id)
                      }
                      onRemove={() => {
                        setRemoveMember(member);
                      }}
                      onConfigureSignatureBlock={() => {
                        clickToLinkHandler(
                          `/${orgId}/deals/${dealId}/deal-legal-entity/${member.id}/signature-block`
                        );
                      }}
                    />
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>
      {!canAddUsers && (
        <ReactTooltip
          id="addUserIsImpossible"
          place="bottom"
          className="tooltip-cls"
        >
          Add CEO first
        </ReactTooltip>
      )}
      <AddPersonToDealForm
        open={!!titleOfMember}
        onClose={() => {
          setTitleOfMember("");
        }}
        title={titleOfMember}
        companyName={companyName}
        onAddExisting={(data) => {
          if (titleOfMember == ADD_COMPANY_COUNSEL_TITLE) {
            createCompanyCounsel(
              orgId,
              dealId,
              data.first_name,
              data.last_name,
              data.email
            );
          } else {
            let legalEntityType: api.LegalEntityType;
            switch (titleOfMember) {
              case "Add Chief Executive Officer":
                legalEntityType = api.LegalEntityType.Chief_Executive_Officer;
                break;
              case "Add Secretary":
                legalEntityType = api.LegalEntityType.Secretary;
                break;
              case "Add Board Member":
                legalEntityType = api.LegalEntityType.Board_Member;
                break;
              case "Add Key Common Holder":
                legalEntityType = api.LegalEntityType.Key_Common_Holder;
                break;
              default:
                break;
            }

            createCompanyMemberForExistingPerson(
              orgId,
              data.person_id,
              legalEntityType
            );
          }
        }}
        onAdd={(data: PersonCreate) => {
          if (titleOfMember == ADD_COMPANY_COUNSEL_TITLE) {
            createCompanyCounsel(
              orgId,
              dealId,
              data.firstName,
              data.lastName,
              data.email
            );
          } else {
            let legalEntityType: api.LegalEntityType;
            switch (titleOfMember) {
              case "Add Chief Executive Officer":
                legalEntityType = api.LegalEntityType.Chief_Executive_Officer;
                break;
              case "Add Secretary":
                legalEntityType = api.LegalEntityType.Secretary;
                break;
              case "Add Board Member":
                legalEntityType = api.LegalEntityType.Board_Member;
                break;
              case "Add Key Common Holder":
                legalEntityType = api.LegalEntityType.Key_Common_Holder;
                break;
              default:
                break;
            }
            createDealCompanyMember(orgId, {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              legalEntityType: legalEntityType,
            });
          }
        }}
        getPersons={searchPersons}
        persons={persons}
      />
      <AddAddress
        open={isOpenAddAddress}
        onClose={() => {
          setIsOpenAddAddress(false);
        }}
        title="Add Company Address"
        companyName={companyName}
        onAdd={(data) => {
          addCompanyAddress(orgId, data);
        }}
      />
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const {
    match: {
      params: { dealId, orgId },
    },
  } = props;
  const { token, user } = state.auth;
  const dealMemberUpdateActionStatus =
    state.actionStatus.DEAL_COMPANY_MEMBER_UPDATE;
  const { updated, formErrors: fieldErrors, persons } = state.deals;
  return {
    token,
    user,
    updated,
    orgId,
    dealId,
    fieldErrors,
    dealMemberUpdateActionStatus,
    persons,
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendPersonInvitation: function (dealId, personId, investorId) {
    dispatch(Actions.sendPersonInvitation(dealId, personId, investorId));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DealCompanyInformationPage);
